/**
 * TanStack Query hooks for `affiliate-manager`.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	affiliateManagerQueryKeys,
	create,
	listByAffiliate,
	listByManager,
	remove,
	type CreateAssignmentBody,
	type CreateAssignmentResponse,
	type ListByAffiliateResponse,
	type ListByManagerResponse,
} from "./api";

export function useAffiliatesByManager(
	managerId: string | null,
): UseQueryResult<ListByManagerResponse, Error> {
	return useQuery({
		queryKey: affiliateManagerQueryKeys.byManager(managerId ?? ""),
		queryFn: () => listByManager(managerId as string),
		enabled: typeof managerId === "string" && managerId.length > 0,
	});
}

export function useAssignmentsByAffiliate(
	affiliateId: string | null,
): UseQueryResult<ListByAffiliateResponse, Error> {
	return useQuery({
		queryKey: affiliateManagerQueryKeys.byAffiliate(affiliateId ?? ""),
		queryFn: () => listByAffiliate(affiliateId as string),
		enabled: typeof affiliateId === "string" && affiliateId.length > 0,
	});
}

export function useAffiliateManagerCreate(): UseMutationResult<
	CreateAssignmentResponse,
	Error,
	CreateAssignmentBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: create,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: affiliateManagerQueryKeys.all });
		},
	});
}

export function useAffiliateManagerDelete(): UseMutationResult<
	{ ok: boolean; id: string },
	Error,
	string
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: remove,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: affiliateManagerQueryKeys.all });
		},
	});
}
